import React from 'react';
import PropTypes from 'prop-types';

import {
  ResponsiveContainer,
  AreaChart as Chart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';

import getChartColors from './chartColors';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0];

  return (
    <Paper
      elevation={3}
      sx={(theme) => ({
        p: 2,
        minWidth: 160,
        backgroundColor: 'var(--color-surface-elevated)',
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Box display="flex" justifyContent="space-between">
        <Typography variant="body2">
          {item.dataKey}
        </Typography>

        <Typography variant="body2" fontWeight={600}>
          {item.value}
        </Typography>
      </Box>
    </Paper>
  );
};

const AreaChart = ({ data, datasets }) => {
  const theme = useTheme();
  const colors = getChartColors(theme, datasets.length || 5);

  return (
    <div className="chart-container">
      <ResponsiveContainer width="100%" height={350}>
        <Chart data={data}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={theme.palette.divider} />

          <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} axisLine={{ stroke: theme.palette.divider }} />
          <YAxis tick={{ fill: theme.palette.text.secondary }} axisLine={{ stroke: theme.palette.divider }} />

          <Tooltip content={<CustomTooltip />} cursor={{ stroke: theme.palette.divider }} />

          {datasets.map((item, index) => (
            <Area
              key={item.key}
              type="monotone"
              dataKey={item.key}
              stroke={colors[index % colors.length]}
              fill={alpha(colors[index % colors.length], 0.2)}
              fillOpacity={0.2}
              strokeWidth={2}
            />
          ))}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
};

AreaChart.propTypes = {
  data: PropTypes.array.isRequired,
  datasets: PropTypes.array.isRequired,
};

export default AreaChart;
